import React, { useEffect, useMemo, useRef, useState } from 'react';

import { widgetsConfigurations } from 'config/widgetConfigurations/widgetsConfigurations';
import { useForm } from 'react-hook-form';

import { IContract, IProject } from 'store/projectContract.store';

import { IState } from 'components/common/GlobalFilterComponent/GlobalFilterComponent';
import { createInitialState } from 'components/common/GlobalFilterComponent/GlobalFilterComponent.utils';
import Icon from 'components/common/Icons/base/Icon/Icon';

import {
    GlobalFiltersState,
    IComponentKey,
    IHashmap,
} from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';
import { Widget } from 'components/Dashboards/Reporting/reporting.types';
import { Button } from 'components/DesignSystem/Button';
import classes from 'components/pages/ConfirmWidgetPage/components/WidgetGlobalFilters1/WidgetGlobalFiltersWrapper/WidgetGlobalFiltersWrapper.module.scss';
import { useProgressMeasurement } from 'components/widgets/SubProgramPreferences/components/progressMeasurement/components/progressMeasurementPreference/useProgressMeasurementPreference';

export const COMPONENT_KEYS_FOR_PROGRESS_MEASUREMENT: IComponentKey[] = [
    'topOverdueOverrunActivities',
    'criticalityWeightedSCurve',
    'highLevelProgramLatest',
    'progressSummary',
];

interface Props {
    children: React.ReactNode;
    defaultValues: GlobalFiltersState;
    setGlobalFilters: React.Dispatch<React.SetStateAction<GlobalFiltersState>>;
    selectedProject: IProject | null;
    selectedContract: IContract | null;
    widget?: Widget;
    componentKey?: string;
}

export const WidgetGlobalFiltersWrapper = ({
    children,
    defaultValues,
    setGlobalFilters,
    selectedProject,
    selectedContract,
    widget,
    componentKey,
}: Props) => {
    const totalFields = useMemo(
        () => React.Children.toArray(children).filter((child) => React.isValidElement(child) && child.props.name),
        [children],
    );
    const methods = useForm({ defaultValues });

    // Controls the state of each filter if it is ready
    const [state, setState] = useState<IState>(createInitialState(totalFields));

    const { handleSubmit, watch, reset } = methods;

    const onFiltersSubmit = () => {
        setGlobalFilters(watch());
    };

    // Build clean defaults from widget configuration (ignoring saved params)
    const buildCleanDefaults = (): GlobalFiltersState => {
        const componentObj = widgetsConfigurations[componentKey as keyof IHashmap];
        if (!componentObj?.globalFiltersConfig) {
            return defaultValues;
        }

        return componentObj.globalFiltersConfig.reduce((acc, { key, defaultValue }) => {
            acc[key] = defaultValue;
            return acc;
        }, {} as GlobalFiltersState);
    };

    const handleClearAll = () => {
        const cleanDefaults = buildCleanDefaults();
        reset(cleanDefaults);
        onFiltersSubmit();
    };

    // Use refs to track previous values of project and contract
    const prevProjectIdRef = useRef<number | undefined>();
    const prevContractIdRef = useRef<number | undefined>();

    function hasProjectOrContractChanged(
        prevProjectId: number | undefined,
        prevContractId: number | undefined,
        currentProjectId: number | undefined,
        currentContractId: number | undefined,
    ): boolean {
        const projectChanged = prevProjectId !== undefined && prevProjectId !== currentProjectId;
        const contractChanged = prevContractId !== undefined && prevContractId !== currentContractId;

        return projectChanged || contractChanged;
    }

    // Clear all values on project contract change
    useEffect(() => {
        if (selectedProject && selectedContract) {
            if (
                hasProjectOrContractChanged(
                    prevProjectIdRef.current,
                    prevContractIdRef.current,
                    selectedProject.id,
                    selectedContract.id,
                )
            ) {
                handleClearAll();
            }

            // Update refs
            prevProjectIdRef.current = selectedProject.id;
            prevContractIdRef.current = selectedContract.id;
        }
    }, [selectedProject?.id, selectedContract?.id]);

    // Only widgets listed in COMPONENT_KEYS_FOR_PROGRESS_MEASUREMENT use `progressMeasurement` global filter.
    // When true, pull the default ProgressMeasurement value.
    const shouldUseProgressMeasurement =
        componentKey !== undefined && COMPONENT_KEYS_FOR_PROGRESS_MEASUREMENT.includes(componentKey as IComponentKey);
    const { value: defaultProgressMeasurement } = useProgressMeasurement(shouldUseProgressMeasurement);

    // Add `progressMeasurement` to widget global filters.
    useEffect(() => {
        if (defaultProgressMeasurement && selectedProject?.id && selectedContract?.id) {
            const editedWidgetProgressMeasurementValue = widget?.params?.progressMeasurement;

            const isProjectOrContractChanged = hasProjectOrContractChanged(
                prevProjectIdRef.current,
                prevContractIdRef.current,
                selectedProject.id,
                selectedContract.id,
            );

            // if project/contract changed set progressMeasurement with default value
            // else use saved progressMeasurement from params
            const value = isProjectOrContractChanged
                ? defaultProgressMeasurement
                : (editedWidgetProgressMeasurementValue ?? defaultProgressMeasurement);
            methods.setValue('progressMeasurement', value);
            onFiltersSubmit();
        }
    }, [defaultProgressMeasurement, selectedProject?.id, selectedContract?.id, widget?.params?.progressMeasurement]);

    return (
        <div className={classes.container}>
            <div className={classes.form}>
                <form onSubmit={handleSubmit(onFiltersSubmit)} className={classes.globalFilterComponentContainer}>
                    {React.Children.map(children, (child) => {
                        return React.isValidElement(child) && child.props.name
                            ? React.createElement(child.type, {
                                  ...{
                                      ...child.props,
                                      methods: methods,
                                      key: child.props.name,
                                      setState: setState,
                                      handleFilters: onFiltersSubmit,
                                  },
                              })
                            : child;
                    })}
                </form>
            </div>
            <div data-testid={'actions'} className={classes.actions}>
                <Button
                    startIcon={<Icon name={'filter-clear'} size={'1.5rem'} color={'var(--button-secondary-fg'} />}
                    variant={'secondary'}
                    onClick={handleClearAll}
                >
                    Clear Filter
                </Button>
            </div>
        </div>
    );
};
